import produce from 'immer';
import { types as storeType } from '../refreshToken/types';
import {
    FilterValueSetPayload,
    IAppConfiguratorInitialState,
    IFilterPayload,
    ISetFilterPayload,
    ISetFilterSubCategoryId,
    ISetGuide,
    ISetLoading,
    ISetPage,
    IToastPayload,
    IToggleCookiesDrawer,
    IToggleAccountModal,
    IToggleInterestModal,
    IToggleCreator,
    IToggleDrawer,
    IToggleNotification,
    types,
    IShareLinks,
} from './types';

const initialState: IAppConfiguratorInitialState = {
    currentPage: 0,
    loading: false,
    drawerStatus: 'closed',
    showSearchBox: 'off',
    selectedFilterCategory: 'video',
    setFilters: {},
    selectedFilterSubCatgory: [],
    selectedFiltersValues: {},
    filterStatus: 'off',
    filterSubCategoryId: null,
    notificationOpen: false,
    accountModalOpen: false,
    creatorModal: undefined,
    showGuide: false,
    guide: [],
    showUserFirstProjectSuggestionBox: false,
    showProjectCreatorGuide: false,
    cookiesDrawerOpen: false,
    showProjectGuide: false,
    isMessageModalExpanded: false,
    avatarMessageOpen: false,
    theme: 'dark',
    subscriptionModalOpen: false,
    interestModalOpen: false,
    signupRoute: 'none',
    formDrafts: {},
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;
    switch (type) {
        case types.SET_PAGE:
            return produce(state, draft => {
                const { pageIndex } = payload as ISetPage;
                draft.currentPage = pageIndex;
            });
        case types.SET_APP_LOADING:
            return produce(state, draft => {
                const { loading } = payload as ISetLoading;
                draft.loading = loading;
            });
        case types.TOGGLE_APP_DRAWER:
            return produce(state, draft => {
                const { status } = payload as IToggleDrawer;
                draft.drawerStatus = status;
            });
        case types.SET_FILTER:
            return produce(state, draft => {
                const { category, categoryPayload } = payload as ISetFilterPayload;
                draft.selectedFilterCategory = category;
                draft.setFilters = categoryPayload;
                draft.selectedFiltersValues = {};
            });
        case types.SET_FILTER_SUB_SELECTION:
            return produce(state, draft => {
                const { id, value, displayName, field } = payload as FilterValueSetPayload;
                const newObject = { ...draft.selectedFiltersValues };
                newObject[id] = {
                    value,
                    displayName,
                    field,
                };
                draft.selectedFiltersValues = newObject;
            });
        case types.CLEAR_FILTER_SUB_SELECTION:
            return produce(state, draft => {
                const { id } = payload as { id: number };
                const newObject = { ...draft.selectedFiltersValues };
                delete newObject[id];
                draft.selectedFiltersValues = newObject;
            });
        case types.CLEAR_FILTER:
            return produce(state, draft => {
                draft.selectedFiltersValues = {};
            });
        case types.TOGGLE_FILTER:
            return produce(state, draft => {
                const { filterStatus, filterId } = payload as IFilterPayload;
                draft.filterStatus = filterStatus;
                draft.filterSubCategoryId = filterId;
            });
        case types.SET_FILTER_SUB_CATEGORY:
            return produce(state, draft => {
                const { id } = payload as ISetFilterSubCategoryId;
                draft.filterSubCategoryId = id;
            });
        case types.TOGGLE_NOTIFICATION:
            return produce(state, draft => {
                const { on } = payload as IToggleNotification;
                draft.notificationOpen = on;
            });
        case types.TOGGLE_ACCOUNT_MODAL:
            return produce(state, draft => {
                const { on } = payload as IToggleAccountModal;
                draft.accountModalOpen = on;
                if (on) {
                    draft.floatingMessageModalOpen = false;
                    draft.avatarMessageOpen = false;
                    draft.messageModalOpen = false;
                }
            });
        case types.TOGGLE_INTEREST_MODAL:
            return produce(state, draft => {
                const { on } = payload as IToggleInterestModal;
                draft.interestModalOpen = on;
            });
        case types.TOGGLE_COOKIES_DRAWER:
            return produce(state, draft => {
                const { on } = payload as IToggleCookiesDrawer;
                draft.cookiesDrawerOpen = on;
            });
        case types.TOGGLE_CREATOR_MODAL:
            return produce(state, draft => {
                const { creatorType } = payload as IToggleCreator;
                draft.creatorModal = creatorType;
            });
        case types.TOGGLE_TOAST:
            return produce(state, draft => {
                const { message, state } = payload as IToastPayload;
                draft.showToast = state;
                draft.toastMessage = message;
            });
        case types.SET_SHOW_GUIDE:
            return produce(state, draft => {
                const { showGuide, page } = payload as ISetGuide;
                draft.showGuide = showGuide;
                if (page) {
                    const guideToShow = draft.guide.find(g => g.page === page);
                    if (guideToShow) {
                        draft.selectedPageGuide = guideToShow;
                    } else {
                        draft.showGuide = false;
                    }
                }
            });
        case types.SET_GUIDE_LINK:
            return produce(state, draft => {
                if (payload.page) {
                    const guidepage = draft.guide?.find(g => g.page === payload.page);
                    if (guidepage) {
                        guidepage.details?.steps.push(payload.step as any);
                    } else {
                        draft.guide?.push(payload);
                    }
                }
            });
        case types.SET_SHOW_SEARCH_BOX:
            return produce(state, draft => {
                draft.showSearchBox = payload;
            });
        case types.SET_SHOW_PROJECT_CREATOR_GUIDE:
            return produce(state, draft => {
                draft.showProjectCreatorGuide = payload;
            });
        case types.SET_SHOW_PROJECT_SUGGESTION_BOX:
            return produce(state, draft => {
                draft.showUserFirstProjectSuggestionBox = payload;
            });
        case types.SET_SHARE_MODAL_STATUS:
            return produce(state, draft => {
                const { status } = payload as IShareLinks;
                draft.shareModalStatus = status;
                draft.sharePayload = payload;
            });
        case storeType.ENQUEUE_FAILED_ACTIONS:
            return produce(state, draft => {
                if (payload && payload.payload && payload.payload.redirect) {
                    draft.currentRedirect = payload.payload.redirect;
                }
            });
        case 'LOGOUT_SUCCESS':
        case types.RESET_REDIRECT:
            return produce(state, draft => {
                draft.currentRedirect = undefined;
                draft.systemForms = undefined;
            });
        case types.SET_PROJECT_GUIDE:
            return produce(state, draft => {
                draft.showProjectGuide = payload;
            });
        case types.TOGGLE_THEME:
            return produce(state, draft => {
                draft.theme = 'dark';
            });
        case types.GET_FILTERS:
            return produce(state, draft => {
                draft.gettingFilter = true;
            });
        case types.GET_FILTERS_SUCCESS:
            return produce(state, draft => {
                draft.gettingFilter = false;
                draft.selectedFilter = payload;
            });
        case types.GET_FILTERS_FAIL:
            return produce(state, draft => {
                draft.gettingFilter = false;
            });
        case types.GET_SYSTEM_FORMS:
            return produce(state, draft => {
                draft.gettingForms = true;
            });
        case types.GET_SYSTEM_FORMS_SUCCESS:
            return produce(state, draft => {
                draft.systemForms = payload;
                draft.gettingForms = false;
            });
        case types.GET_SYSTEM_FORMS_FAIL:
            return produce(state, draft => {
                draft.gettingForms = false;
                draft.formsError = payload;
            });
        case types.SET_RECOMMENDATION:
            return produce(state, draft => {
                draft.recommendation = payload;
            });
        case types.SET_SYSTEM_FORM:
            return produce(state, draft => {
                draft.currentSystemForm = payload;
            });
        case types.TOGGLE_SUBSCRIPTION_MODAL:
            return produce(state, draft => {
                if (typeof payload === 'boolean' || payload == null) {
                    draft.subscriptionModalOpen = Boolean(payload);
                    if (!payload) {
                        draft.subscriptionModalEntitlementFilter = undefined;
                        draft.subscriptionModalAllowedPlanClasses = undefined;
                    }
                    return;
                }
                draft.subscriptionModalOpen = payload.open;
                draft.subscriptionModalEntitlementFilter = payload.open ? payload.requireAnyEntitlement : undefined;
                draft.subscriptionModalAllowedPlanClasses = payload.open ? payload.allowedPlanClasses : undefined;
            });
        case types.TOGGLE_MESSAGE_MODAL:
            return produce(state, draft => {
                draft.messageModalOpen = payload;
            });

        case types.TOGGLE_FLOATING_MESSAGE_MODAL:
            return produce(state, draft => {
                draft.floatingMessageModalOpen = payload;
            });

        case types.TOGGLE_EXPAND_MESSAGE_MODAL:
            return produce(state, draft => {
                draft.isMessageModalExpanded = payload;
            });

        case types.TOGGLE_AVATAR_MESSAGE_MODAL:
            return produce(state, draft => {
                draft.avatarMessageOpen = payload;
            });

        case types.SET_MESSAGES_EXPANDED_RETURN_TARGET:
            return produce(state, draft => {
                draft.messagesExpandedReturnTarget = payload;
            });

        case types.CLOSE_EPHEMERAL_MESSAGE_UI:
            return produce(state, draft => {
                draft.floatingMessageModalOpen = false;
                draft.avatarMessageOpen = false;
                draft.messageModalOpen = false;
                draft.messagesExpandedReturnTarget = undefined;
            });

        case types.SET_SIGNUP_ROUTE:
            return produce(state, draft => {
                draft.signupRoute = payload;
            });
        case types.SAVE_FORM_DRAFT:
            return produce(state, draft => {
                const { formType, formData } = payload;
                if (!draft.formDrafts) {
                    draft.formDrafts = {};
                }
                draft.formDrafts[formType] = formData;
            });
        case types.LOAD_FORM_DRAFT:
            return state; // Loading is handled by selectors
        case types.CLEAR_FORM_DRAFT:
            return produce(state, draft => {
                const { formType } = payload;
                if (draft.formDrafts && draft.formDrafts[formType]) {
                    delete draft.formDrafts[formType];
                }
            });
        case types.CLEAR_ALL_DRAFTS:
            return produce(state, draft => {
                draft.formDrafts = {};
            });
        case 'LOGOUT':
            return produce(state, draft => {
                /** Reset System forms and drafts */
                draft.systemForms = [];
                draft.formDrafts = {};
            });
        default:
            return state;
    }
};

export { reducer };
